import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Zap, Pin } from "lucide-react";

export function BankDirectBookCard({ companyId, tx, onBooked }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [learned, setLearned] = useState(null);
  const [saveRule, setSaveRule] = useState(false);

  useEffect(() => {
    setLearned(null);
    setText("");
    setSuggestions([]);
    setSelected(null);
    setSaveRule(false);
    if (!tx?.journal_entry_id) return;
    apiClient
      .get(`/bank-transactions/${tx.journal_entry_id}/account-suggestion`)
      .then((r) => { if (r.data?.found) setLearned(r.data); })
      .catch(() => {});
  }, [tx?.journal_entry_id]);

  const bookAccount = async (accountId, note) => {
    setBooking(true);
    try {
      const r = await apiClient.post(`/bank-transactions/${tx.journal_entry_id}/book-to-account`, {
        account_id: accountId,
        note: note || "",
        save_rule: saveRule,
      });
      toast.success(`Verbucht auf Konto ${r.data.account_id} ✓ (CHF ${r.data.amount})`);
      if (r.data.rule_saved) {
        toast.success(`📌 Regel gespeichert: «${r.data.party}» wird künftig immer mit Konto ${r.data.account_id} vorgeschlagen`);
      } else if (r.data.rule_hint) {
        const h = r.data.rule_hint;
        toast(`«${h.vendor_name}» wurde nun ${h.count}× auf Konto ${h.account_id} gebucht`, {
          description: "Als feste Regel übernehmen? Künftige Zahlungen werden dann automatisch so vorgeschlagen — jederzeit änderbar.",
          duration: 12000,
          action: {
            label: "📌 Übernehmen",
            onClick: async () => {
              try {
                await apiClient.post(`/companies/${companyId}/bank/booking-rules`, {
                  vendor_name: h.vendor_name,
                  account_id: h.account_id,
                });
                toast.success(`📌 Regel gespeichert: «${h.vendor_name}» → Konto ${h.account_id}`);
              } catch (err) {
                toast.error("Regel konnte nicht gespeichert werden");
              }
            },
          },
        });
      }
      onBooked && onBooked();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verbuchen fehlgeschlagen");
    } finally {
      setBooking(false);
    }
  };

  const askAi = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    setSuggestions([]);
    setSelected(null);
    try {
      const r = await apiClient.post(`/companies/${companyId}/accounts/ai-suggest`, {
        text: text.trim(),
      });
      const items = r.data || [];
      setSuggestions(items);
      if (items.length > 0) setSelected(items[0].code);
      else toast.info("Kein passendes Konto gefunden — anders beschreiben.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Vorschlag fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const book = async () => {
    if (!selected) return;
    await bookAccount(selected, text.trim());
    setText("");
    setSuggestions([]);
    setSelected(null);
  };

  return (
    <div
      className="mt-4 pt-4 border-t border-slate-200"
      data-testid="bank-direct-book-card"
    >
      <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
        <Sparkles className="h-3 w-3 text-violet-600" />
        Ohne Beleg direkt verbuchen
      </div>
      {learned && (
        <button
          type="button"
          onClick={() => bookAccount(learned.account_code || learned.account_id, learned.party)}
          disabled={booking}
          className="w-full text-left rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 text-sm transition flex items-center justify-between gap-2 mb-2"
          data-testid="bank-direct-learned-suggestion"
          title="Das System hat gelernt, wie du ähnliche Zahlungen früher gebucht hast — ein Klick bucht genauso"
        >
          <div className="min-w-0">
            <span className="text-emerald-700 font-medium">
              {learned.source === "rule" ? "📌 Feste Regel:" : "🧠 Aus deiner Historie:"}
            </span>{" "}
            <span className="font-mono font-semibold">{learned.account_code}</span>{" "}
            <span className="text-slate-700">{learned.account_name}</span>
            <div className="text-xs text-emerald-700/80 truncate">
              {learned.hint} {learned.party ? `· ${learned.party}` : ""}
            </div>
          </div>
          <Badge className="bg-emerald-600 text-white flex-shrink-0 text-[10px]">
            {booking ? "Buche…" : "1-Klick buchen"}
          </Badge>
        </button>
      )}
      <div className="flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); askAi(); } }}
          placeholder='Beschreibe die Zahlung: "Tankstelle", "Teamlunch", "Marketing"…'
          data-testid="bank-direct-text-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={askAi}
          disabled={loading || !text.trim()}
          className="text-violet-700 border-violet-300 hover:bg-violet-50 flex-shrink-0"
          data-testid="bank-direct-ask-btn"
          title="Beschreibe kurz, was die Zahlung war — die KI schlägt das passende Buchhaltungskonto vor"
        >
          {loading ? "Frage KI…" : "KI fragen"}
        </Button>
      </div>
      <label
        className="mt-2 flex items-center gap-2 text-xs text-slate-600 cursor-pointer"
        title="Merkt sich die Gegenpartei und schlägt bei künftigen Zahlungen automatisch dieses Konto vor — du bestätigst weiterhin mit einem Klick. Die Regel ist jederzeit unter «Buchungsregeln» entfernbar."
        data-testid="bank-direct-save-rule"
      >
        <input
          type="checkbox"
          checked={saveRule}
          onChange={(e) => setSaveRule(e.target.checked)}
          className="accent-slate-900"
        />
        <Pin className="h-3 w-3 text-slate-400" />
        Immer so buchen (Gegenpartei → Konto merken, jederzeit änderbar)
      </label>
      {suggestions.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid="bank-direct-suggestions">
          {suggestions.map((s) => (
            <button
              key={s.code}
              type="button"
              onClick={() => setSelected(s.code)}
              className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition flex items-center justify-between gap-2 ${
                selected === s.code
                  ? "border-violet-400 bg-violet-100"
                  : "border-violet-200 bg-violet-50/50 hover:bg-violet-50"
              }`}
              data-testid={`bank-direct-suggestion-${s.code}`}
            >
              <div className="min-w-0">
                <span className="font-mono font-semibold">{s.code}</span>{" "}
                <span className="text-slate-700">{s.name}</span>
                <div className="text-xs text-slate-500 truncate">{s.reason}</div>
              </div>
              <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-700 flex-shrink-0">
                {Math.round((s.confidence || 0) * 100)}%
              </Badge>
            </button>
          ))}
          <Button
            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
            onClick={book}
            disabled={!selected || booking}
            data-testid="bank-direct-book-btn"
          >
            <Zap className="h-3.5 w-3.5 mr-1.5" />
            {booking ? "Verbuche…" : `Auf Konto ${selected || "…"} verbuchen`}
          </Button>
        </div>
      )}
    </div>
  );
}
